import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence, m } from "framer-motion";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";

interface IconPickerProps {
  options: Record<string, IconProp>;
  value: string | undefined | null;
  onChange: (value: string | null) => void;
  placeholder?: IconProp;
  disabled?: boolean;
  dataLoc?: string;
  size?: "md" | "lg";
  allowClear?: boolean;
}

const DEFAULT_PLACEHOLDER_ICON = faGear;

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
  dataLoc,
  size = "md",
  allowClear = false,
}) => {
  const hasSelectedIcon = !!value && value in options;
  const selectedIcon = hasSelectedIcon
    ? (options[value] as IconProp)
    : (placeholder ?? DEFAULT_PLACEHOLDER_ICON);

  return (
    <Listbox disabled={disabled} value={value} horizontal onChange={onChange}>
      {({ open }) => (
        <>
          <ListboxButton
            className={twJoin(
              "flex shrink-0 items-center justify-center border border-gray-200 bg-white focus:outline-none data-[hover]:bg-gray-50",
              size === "md" && "h-8 w-8 rounded-lg",
              size === "lg" && "h-15 w-15 rounded-xl",
            )}
            data-loc={dataLoc}
          >
            <m.div
              key={value}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: size === "md" ? -7 : -14, opacity: 0 }}
              initial={{ y: size === "md" ? 7 : 14, opacity: 0 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <Icon
                color={hasSelectedIcon ? "text-gray-600" : "text-gray-300"}
                icon={selectedIcon}
                size={size === "md" ? "sm" : "2xl"}
              />
            </m.div>
          </ListboxButton>

          <AnimatePresence initial={false}>
            {open && (
              <ListboxOptions
                anchor={{
                  to: "bottom",
                  gap: 6,
                  padding: 8,
                }}
                as={MotionDropdownPanel}
                className="focus:outline-none"
              >
                <div className="grid grid-cols-3 gap-2 px-2 text-gray-600">
                  {allowClear && (
                    <ListboxOption
                      key="clear"
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-gray-400 data-[focus]:bg-gray-50 data-[selected]:bg-gray-100"
                      value={null}
                    >
                      <Icon
                        icon={placeholder ?? DEFAULT_PLACEHOLDER_ICON}
                        size="sm"
                      />
                    </ListboxOption>
                  )}
                  {Object.entries(options).map(([key, icon], index) => (
                    <ListboxOption
                      key={key}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg data-[focus]:bg-gray-50 data-[selected]:bg-gray-100"
                      data-loc={dataLoc && `${dataLoc}-icon-${index}`}
                      value={key}
                    >
                      <Icon icon={icon} size="sm" />
                    </ListboxOption>
                  ))}
                </div>
              </ListboxOptions>
            )}
          </AnimatePresence>
        </>
      )}
    </Listbox>
  );
};
